import PropTypes from "prop-types"

// @mui material components
import Icon from "@mui/material/Icon"
import { Grid, Tooltip, Chip } from "@mui/material"

// Material Dashboard 2 React components
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import MDButton from "components/MDButton"

// Context
import { useMaterialUIController } from "context"

// React Router
import { useNavigate } from "react-router-dom"

// Services
import { deleteConfiguracion } from "layouts/asistencia/configuraciones/components/add/service"

function ConfiguracionItem({
  id,
  dependenciaNombre,
  tipoAsistenteNombre,
  diaSemana,
  horaEntradaEsperada,
  horaSalidaEsperada,
  minutosToleranciaEntrada,
  minutosToleranciaSalida,
  activo,
  noGutter,
  onReload,
}) {
  const [controller] = useMaterialUIController()
  const { darkMode } = controller
  const navigate = useNavigate()

  const handleEdit = (idConfiguracion) => {
    navigate(`/asistencia/configuraciones/${idConfiguracion}`)
  }

  const handleDelete = async (idConfiguracion) => {
    try {
      const deleted = await deleteConfiguracion(idConfiguracion)
      if (deleted) {
        onReload()
      }
    } catch (error) {
      console.error("Error deleting configuración:", error)
    }
  }

  // Helper function to format day names
  const formatDiaSemana = (dia) => {
    const dias = {
      LUNES: "Lunes",
      MARTES: "Martes",
      MIERCOLES: "Miércoles",
      JUEVES: "Jueves",
      VIERNES: "Viernes",
      SABADO: "Sábado",
      DOMINGO: "Domingo",
    }
    return dias[dia] || dia
  }

  return (
    <MDBox
      id={id}
      component="li"
      display="flex"
      justifyContent="space-between"
      alignItems="flex-start"
      bgColor={darkMode ? "transparent" : "grey-100"}
      borderRadius="lg"
      p={3}
      mb={noGutter ? 0 : 1}
      mt={2}
    >
      <MDBox width="100%" display="flex" flexDirection="column">
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          flexDirection={{ xs: "column", sm: "row" }}
          mb={2}
        >
          <Grid container spacing={0}>
            <Grid item xs={12} md={12}>
              <MDBox display="flex" alignItems="center" gap={1}>
                <MDTypography variant="h6" fontWeight="medium" textTransform="capitalize">
                  {formatDiaSemana(diaSemana)} - {dependenciaNombre}
                </MDTypography>
                <Chip
                  label={activo ? "Activo" : "Inactivo"}
                  color={activo ? "success" : "default"}
                  size="small"
                />
              </MDBox>
            </Grid>
            {tipoAsistenteNombre && (
              <Grid item xs={12} md={12}>
                <MDTypography variant="caption" fontWeight="light">
                  TIPO DE ASISTENTE: {tipoAsistenteNombre}
                </MDTypography>
              </Grid>
            )}
          </Grid>
          <MDBox display="flex" alignItems="center" mt={{ xs: 2, sm: 0 }} ml={{ xs: -1.5, sm: 0 }}>
            <MDBox mr={1}>
              <Tooltip title="Eliminar configuración">
                <MDButton variant="text" color="error" onClick={() => handleDelete(id)}>
                  <Icon>delete</Icon>&nbsp;Eliminar
                </MDButton>
              </Tooltip>
            </MDBox>
            <Tooltip title="Editar configuración">
              <MDButton
                data-id={id}
                variant="text"
                color={darkMode ? "white" : "dark"}
                onClick={() => handleEdit(id)}
              >
                <Icon>edit</Icon>&nbsp;Editar
              </MDButton>
            </Tooltip>
          </MDBox>
        </MDBox>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <MDBox mb={1} lineHeight={0}>
              <MDTypography variant="caption" color="text">
                Hora de Entrada:&nbsp;&nbsp;&nbsp;
                <MDTypography variant="caption" fontWeight="medium">
                  {horaEntradaEsperada}
                </MDTypography>
              </MDTypography>
            </MDBox>
            <MDBox mb={1} lineHeight={0}>
              <MDTypography variant="caption" color="text">
                Tolerancia Entrada:&nbsp;&nbsp;&nbsp;
                <MDTypography variant="caption" fontWeight="medium">
                  {minutosToleranciaEntrada} minutos
                </MDTypography>
              </MDTypography>
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6}>
            <MDBox mb={1} lineHeight={0}>
              <MDTypography variant="caption" color="text">
                Hora de Salida:&nbsp;&nbsp;&nbsp;
                <MDTypography variant="caption" fontWeight="medium">
                  {horaSalidaEsperada}
                </MDTypography>
              </MDTypography>
            </MDBox>
            <MDBox mb={1} lineHeight={0}>
              <MDTypography variant="caption" color="text">
                Tolerancia Salida:&nbsp;&nbsp;&nbsp;
                <MDTypography variant="caption" fontWeight="medium">
                  {minutosToleranciaSalida} minutos
                </MDTypography>
              </MDTypography>
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
    </MDBox>
  )
}

// Default values
ConfiguracionItem.defaultProps = {
  noGutter: false,
  onReload: () => {},
  tipoAsistenteNombre: null,
}

// PropTypes
ConfiguracionItem.propTypes = {
  id: PropTypes.number.isRequired,
  dependenciaNombre: PropTypes.string.isRequired,
  tipoAsistenteNombre: PropTypes.string,
  diaSemana: PropTypes.string.isRequired,
  horaEntradaEsperada: PropTypes.string.isRequired,
  horaSalidaEsperada: PropTypes.string.isRequired,
  minutosToleranciaEntrada: PropTypes.number.isRequired,
  minutosToleranciaSalida: PropTypes.number.isRequired,
  activo: PropTypes.bool.isRequired,
  noGutter: PropTypes.bool,
  onReload: PropTypes.func,
}

export default ConfiguracionItem
